// One social sign-in button (and the Apple slot placeholder) shared by sign-in and accept-invite (BL-2776).
// Each page picks its own look through `variant`, so the markup lives here once.

import clsx from 'clsx'
import type { SocialProviderId } from '@auctogon/core-admin/auth/social-providers'
import { Spinner } from '@auctogon/ui/web'

import { AppleIcon, GoogleIcon } from './SocialProviderIcons'

const PROVIDER_LABEL: Record<SocialProviderId, string> = { google: 'Google', apple: 'Apple' }

// One height for the button and its placeholder, so they cannot drift apart.
const BUTTON_HEIGHT_CLASS = 'min-h-[50px]'

const VARIANT_TEST_ID_PREFIX = { signIn: 'sign-in', acceptInvite: 'accept-invite' } as const

const VARIANT_CLASS = {
  signIn:
    'bg-white border border-gray-200 hover:border-gray-300 text-gray-900 font-semibold rounded-[10px] transition active:scale-[0.98]',
  acceptInvite:
    'border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors',
} as const

/** Which page's look the button takes. */
export type SocialSignInVariant = keyof typeof VARIANT_CLASS

/** Props for {@link SocialSignInButton}. */
export interface SocialSignInButtonProps {
  provider: SocialProviderId
  /** True while this provider's sign-in is in flight (shows the spinner). */
  loading: boolean
  /** True while any sign-in action is in flight. */
  disabled: boolean
  onClick: () => void
  variant: SocialSignInVariant
  /** Spacing the page needs around the button. */
  className?: string
}

/**
 * A full-width "Continue with <provider>" button.
 *
 * @param props - see {@link SocialSignInButtonProps}.
 * @returns The button element.
 */
export function SocialSignInButton({
  provider, loading, disabled, onClick, variant, className,
}: SocialSignInButtonProps) {
  const icon = provider === 'apple' ? <AppleIcon /> : <GoogleIcon />
  return (
    <button
      type="button"
      data-testid={`${VARIANT_TEST_ID_PREFIX[variant]}-${provider}-button`}
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'w-full py-3 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed',
        BUTTON_HEIGHT_CLASS,
        VARIANT_CLASS[variant],
        className,
      )}
    >
      {loading ? <Spinner size="sm" decorative /> : icon}
      Continue with {PROVIDER_LABEL[provider]}
    </button>
  )
}

/**
 * Holds the Apple button's space while the providers read settles, so the
 * button does not shift the layout when it appears. Same height as the button.
 *
 * @param props - `className` carries the page's spacing, matching the button's.
 * @returns An empty, aria-hidden block.
 */
export function SocialSignInPlaceholder({ className }: { className?: string }) {
  return <div aria-hidden="true" data-testid="social-signin-placeholder" className={clsx(BUTTON_HEIGHT_CLASS, className)} />
}
